const STRIP_PARAMS = new Set(["fbclid", "ref"]);

function shouldStripParam(key) {
  const lower = key.toLowerCase();
  return lower.startsWith("utm_") || STRIP_PARAMS.has(lower);
}

/**
 * Normalize a chapter URL so equivalent URLs map to the same cache key.
 * 1. Lowercase scheme and host; drop #hash.
 * 2. Drop utm_*, fbclid, and ref query params; sort remaining params.
 * 3. Strip trailing "/" (except at the root "/").
 * @param {string} rawUrl
 * @returns {string}
 */
export function normalizeUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") {
    return "";
  }
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return "";
  }
  try {
    const parsed = new URL(trimmed);
    parsed.hash = "";

    const keptEntries = [];
    for (const [key, value] of parsed.searchParams.entries()) {
      if (!shouldStripParam(key)) {
        keptEntries.push([key, value]);
      }
    }
    keptEntries.sort((a, b) => {
      if (a[0] !== b[0]) {
        return a[0].localeCompare(b[0]);
      }
      return a[1].localeCompare(b[1]);
    });

    parsed.search = "";
    for (const [key, value] of keptEntries) {
      parsed.searchParams.append(key, value);
    }

    if (parsed.pathname.length > 1 && parsed.pathname.endsWith("/")) {
      parsed.pathname = parsed.pathname.replace(/\/+$/, "") || "/";
    }

    return parsed.toString();
  } catch (e) {
    return trimmed;
  }
}

/**
 * Resolve a relative or absolute navigation link against a base chapter URL,
 * returning a normalized absolute URL (or null if link is empty).
 * @param {string|null|undefined} link
 * @param {string} chapterUrl
 * @returns {string|null}
 */
export function resolveLink(link, chapterUrl) {
  if (!link || typeof link !== "string" || !link.trim()) {
    return null;
  }
  if (!chapterUrl || typeof chapterUrl !== "string" || !chapterUrl.trim()) {
    return normalizeUrl(link);
  }
  try {
    const resolved = new URL(link.trim(), chapterUrl.trim()).toString();
    return normalizeUrl(resolved);
  } catch (e) {
    return null;
  }
}

/**
 * Compute the normalized Firestore document ID for a chapter URL.
 * @param {string} rawUrl
 * @returns {string}
 */
export function chapterIdFor(rawUrl) {
  return normalizeUrl(rawUrl).replace(/\//g, "_");
}

/**
 * Compute the legacy Firestore document ID (raw URL with "/" replaced by "_").
 * @param {string} rawUrl
 * @returns {string}
 */
export function legacyChapterIdFor(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") {
    return "";
  }
  return rawUrl.trim().replace(/\//g, "_");
}
